/** Supplier Governance tab (Demo Mode only, local-only - no backend persistence yet).
 * Surfaces ESG info, certifications, insurance, contract/renewal, delivery rules, price
 * lists, approval/rebate and performance/data-quality for a supplier, plus a "quick
 * onboarding" mode for very small suppliers that skips most of the above. See
 * `src/mocks/data/supplierGovernanceData.ts`. */

import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import ExceptionBanner from '../../../../components/shared/ExceptionBanner';
import { evaluateRules } from '../../../../utils/rulesEngine';
import { UserContext } from '../../../../components/shared/useUser';
import { isDemoModeEnabled } from '../../../../utils/featureAccess';
import {
  SupplierApprovalStatus,
  SupplierGovernanceProfile,
  SupplierOnboardingTier,
} from '../../../../mocks/data/supplierGovernanceData';
import DueDiligenceSection from './governance/DueDiligenceSection';
import DeliveryAndPricingSection from './governance/DeliveryAndPricingSection';

const APPROVAL_STATUS_COLOR: Record<SupplierApprovalStatus, 'success' | 'warning' | 'error' | 'default'> = {
  [SupplierApprovalStatus.Approved]: 'success',
  [SupplierApprovalStatus.AutoApprovedLowVolume]: 'success',
  [SupplierApprovalStatus.PendingReview]: 'warning',
  [SupplierApprovalStatus.Rejected]: 'error',
  [SupplierApprovalStatus.Suspended]: 'error',
};

export interface GovernanceTabProps {
  profile: SupplierGovernanceProfile;
  quickOnboard: boolean;
  onQuickOnboardChange: (quickOnboard: boolean) => void;
  quickOnboardDisabled?: boolean;
}

const GovernanceTab: React.FC<GovernanceTabProps> = ({ profile, quickOnboard, onQuickOnboardChange, quickOnboardDisabled }) => {
  const { t } = useTranslation('suppliers');
  const { hasPermissionTo } = useContext(UserContext);
  const [approvedByProcurement, setApprovedByProcurement] = useState(false);
  const canApprove = hasPermissionTo(['SupplierGovernanceApprove']);
  const isApproved = approvedByProcurement
    || profile.approvalStatus === SupplierApprovalStatus.Approved
    || profile.approvalStatus === SupplierApprovalStatus.AutoApprovedLowVolume;

  const exceptions = evaluateRules({
    supplier: {
      isNew: true,
      certificationExpiryDate: profile.certifications.find((cert) => cert.status === 'Expired')?.expiryDate,
    },
  });

  return (
    <Stack spacing={5}>
      <FormControlLabel
        control={(
          <Switch
            checked={quickOnboard}
            disabled={quickOnboardDisabled}
            onChange={(event) => onQuickOnboardChange(event.target.checked)}
          />
        )}
        label={(
          <Box>
            <Typography variant="textSM"
              fontWeight={600}>
              {t('quickOnboarding', 'Quick onboarding (very small supplier)')}
            </Typography>
            <Typography variant="textXS"
              color="text.secondary">
              {t('quickOnboardingDesc', 'Skips ESG/certification/contract capture and auto-approves at low order volume. Use for one-off or very small local suppliers.')}
            </Typography>
          </Box>
        )}
      />

      <ExceptionBanner exceptions={exceptions} />

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('approvalStatus', 'Approval status')}
          </Typography>
          <Stack direction="row"
            alignItems="center"
            spacing={2}>
            <Chip
              label={approvedByProcurement ? SupplierApprovalStatus.Approved : profile.approvalStatus}
              color={approvedByProcurement ? 'success' : APPROVAL_STATUS_COLOR[profile.approvalStatus]}
              size="small"
            />
            {profile.onboardingTier === SupplierOnboardingTier.QuickOnboard && (
              <Chip
                label={t('quickOnboardTier', 'Quick onboard')}
                color="default"
                size="small"
              />
            )}
            {!isApproved && canApprove && (
              <Button variant="primary"
                size="md"
                onClick={() => setApprovedByProcurement(true)}>
                {t('approveSupplier', 'Approve supplier (Procurement)')}
              </Button>
            )}
            {!isApproved && !canApprove && isDemoModeEnabled() && (
              <Typography variant="textXS"
                color="text.secondary">
                {t('requiresProcurementRole', 'Requires the Procurement demo role to approve.')}
              </Typography>
            )}
          </Stack>
        </Box>
      </Section>

      {!quickOnboard && (
        <>
          <DueDiligenceSection profile={profile} />
          <DeliveryAndPricingSection profile={profile} />
        </>
      )}

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('performanceDataQuality', 'Performance & data quality')}
          </Typography>
          {profile.performance ? (
            <Grid container
              spacing={2}
              sx={{ mb: 3 }}>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('onTimeDelivery', 'On-time delivery')}</Typography>
                <Typography variant="textSM">{profile.performance.onTimeDeliveryPercentage}%</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('fillRate', 'Fill rate')}</Typography>
                <Typography variant="textSM">{profile.performance.fillRatePercentage}%</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('qualityScore', 'Quality score')}</Typography>
                <Typography variant="textSM">{profile.performance.qualityScore} / 5</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trend', 'Trend')}</Typography>
                <Chip label={profile.performance.scoreTrend}
                  size="small"
                  color={profile.performance.scoreTrend === 'Down' ? 'error' : profile.performance.scoreTrend === 'Up' ? 'success' : 'default'} />
              </Grid>
            </Grid>
          ) : (
            <Typography variant="textSM"
              sx={{ mb: 3 }}>{t('noPerformanceData', 'No performance history yet - this is a new supplier.')}</Typography>
          )}

          {profile.dataQualityFlags.length > 0 && (
            <Stack spacing={1}>
              {profile.dataQualityFlags.map((flag) => (
                <Chip
                  key={flag.id}
                  label={`${flag.field}: ${flag.issue}`}
                  color={flag.severity === 'Blocking' ? 'error' : flag.severity === 'Warning' ? 'warning' : 'default'}
                  size="small"
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Section>
    </Stack>
  );
};

export default GovernanceTab;
